// @flow
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { TextBlock, TextInput, YearMonthRangePopover, Button } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { rgba } from 'color-helpers';
import { getISODateString, getLastDateOfMonth, toDate } from 'date-functions';
import debounce from 'lodash/debounce';
import React from 'react';
import styled from 'styled-components';

import { ClientRulesSidebar } from 'bk-web-app/client-rules-sidebar';
import type { DateRange, TransactionManagerTab } from './types';
import transactionManagerTabs from './transactionManagerTabs';

const getFormattedDateString = dateString => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
};

const TransactionManagerHeaderLayout = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const TransactionManagerControlsLayout = styled.div`
  display: grid;
  grid-template-columns: max-content 180px 320px 100px;
  grid-column-gap: 24px;
  left: 0;
  padding: 24px 32px 0;
  position: absolute;
  top: 0;
  right: 0;
`;

const TransactionManagerNavigationLayout = styled.div`
  border-bottom: 1px solid ${colors.silt};
  bottom: 0;
  display: flex;
  left: 0;
  padding: 0 8px;
  position: absolute;
  right: 0;
`;

const Tab = styled.button`
  background: transparent;
  border: 0 none;
  border-radius: 0;
  box-shadow: none;
  color: ${colors.dust};
  cursor: pointer;
  display: block;
  font-size: 16px;
  line-height: 16px;
  padding: 8px 24px;
  position: static;

  &:focus {
    background: ${rgba(colors.brook, 0.75)};
  }

  ${({ isActive }) =>
    isActive &&
    `
    box-shadow: 0 1px ${colors.water};
    color: ${colors.water};
  `};
`;

const TabBadge = styled.span`
  background: ${colors.water};
  border-radius: 10px;
  color: white;
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 20px;
  margin-left: 8px;
  min-width: 20px;
  padding: 0 6px;
  text-align: center;
`;

type TransactionManagerHeaderProps = {
  dateRange: DateRange,
  search: string,
  updateQueryParams: Object => void,
  tab: TransactionManagerTab,
  personalTransactionCount?: number,
  isPersonalBusinessEnabled?: boolean,
};

const TransactionManagerHeader = ({
  dateRange: { endDate, startDate },
  search,
  updateQueryParams,
  tab,
  personalTransactionCount,
  isPersonalBusinessEnabled,
}: TransactionManagerHeaderProps) => {
  // If there's a start date, get the year portion, otherwise get whatever year it is locally
  const defaultDisplayedYear = startDate
    ? Number(startDate.split('-')[0])
    : new Date().getFullYear();
  // If there are start or end dates, automatically select them when the date popup opens
  const defaultStartYearMonth = startDate && startDate.substr(0, 7);
  const defaultEndYearMonth = endDate && endDate.substr(0, 7);
  // Get a formatted representation of the date range to display in the text input
  const dateRangeText =
    startDate && endDate
      ? `${getFormattedDateString(startDate)} – ${getFormattedDateString(endDate)}`
      : 'No date range selected';
  const handleRangeSelect = (startYearMonth: string | null, endYearMonth: string | null) => {
    if (!startYearMonth || !endYearMonth) {
      return;
    }
    updateQueryParams({
      endDate: getISODateString(getLastDateOfMonth(endYearMonth)),
      startDate: getISODateString(toDate(startYearMonth)),
    });
  };

  const updateSearchParam = debounce((updatedSearch: string) => {
    updateQueryParams({ search: updatedSearch });
  }, 300);

  return (
    <TransactionManagerHeaderLayout>
      <TransactionManagerControlsLayout>
        <TextBlock aria-label="Transaction Manager Page" styleType="title">
          Transaction Manager
        </TextBlock>
        <YearMonthRangePopover
          defaultDisplayedYear={defaultDisplayedYear}
          defaultStartYearMonth={defaultStartYearMonth}
          defaultEndYearMonth={defaultEndYearMonth}
          onRangeSelect={handleRangeSelect}
        >
          {showPopover => (
            <TextInput onFocus={() => showPopover(true)} styleType="ghost" value={dateRangeText} />
          )}
        </YearMonthRangePopover>
        <TextInput
          defaultValue={search}
          onChange={event => updateSearchParam(event.target.value)}
          placeholder="Search transactions, comments, and notes"
        />
        <AppSidebar>
          {({ closeSidebar, openSidebar }) => (
            <Button
              size="medium"
              styleType="primary"
              onClick={() => openSidebar(<ClientRulesSidebar dismiss={closeSidebar} />)}
            >
              New Rule
            </Button>
          )}
        </AppSidebar>
      </TransactionManagerControlsLayout>
      <TransactionManagerNavigationLayout>
        <Tab
          isActive={tab === transactionManagerTabs.FOLLOW_UP}
          onClick={() => updateQueryParams({ tab: transactionManagerTabs.FOLLOW_UP })}
        >
          Follow Up
        </Tab>
        <Tab
          isActive={tab === transactionManagerTabs.CATEGORIZATION}
          onClick={() => updateQueryParams({ tab: transactionManagerTabs.CATEGORIZATION })}
        >
          Categorization
        </Tab>
        <Tab
          isActive={tab === transactionManagerTabs.TRANSACTIONS1099}
          onClick={() => updateQueryParams({ tab: transactionManagerTabs.TRANSACTIONS1099 })}
        >
          1099 Transactions
        </Tab>
        <Tab
          isActive={tab === transactionManagerTabs.ISSUES}
          onClick={() => updateQueryParams({ tab: transactionManagerTabs.ISSUES })}
        >
          Transaction Issues
        </Tab>
        <Tab
          isActive={tab === transactionManagerTabs.FINANCIAL_PACKAGE_NOTES}
          onClick={() => updateQueryParams({ tab: transactionManagerTabs.FINANCIAL_PACKAGE_NOTES })}
        >
          Financial Package Notes
        </Tab>
        <Tab
          isActive={tab === transactionManagerTabs.PERSONAL_TRANSACTIONS}
          onClick={() => updateQueryParams({ tab: transactionManagerTabs.PERSONAL_TRANSACTIONS })}
        >
          Personal Transactions
          {isPersonalBusinessEnabled && personalTransactionCount && personalTransactionCount > 0 ? (
            <TabBadge>{personalTransactionCount}</TabBadge>
          ) : null}
        </Tab>
      </TransactionManagerNavigationLayout>
    </TransactionManagerHeaderLayout>
  );
};

export default TransactionManagerHeader;
